import { useEffect, useMemo, useRef, useState } from 'react';
import { PREVIEW_OLD_MS, drawContain, type PreviewSnapshot } from './preview';
import { blit, lowTraffic, onLowTraffic, setLowTraffic, sharedPreview } from './previewHub';
import type { LiveViewSnapshot } from './liveView';
import { t } from '../i18n';
import { Icon } from '../ui';

export type PreviewState = 'fresh' | 'old' | 'paused' | 'waiting' | 'unsupported';

/** Shown state of the preview; null = hidden (the stream is offline). */
export function previewState(s: PreviewSnapshot | undefined, now: number): PreviewState | null {
  if (!s) return 'waiting';
  if (s.mode === 'unsupported') return 'unsupported';
  if (s.offline) return null;
  if (s.paused) return 'paused';
  if (!s.hasFrame || s.frameAt === undefined) return 'waiting';
  return now - s.frameAt > PREVIEW_OLD_MS ? 'old' : 'fresh';
}

function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 120 ? `${s} s` : `${Math.round(s / 60)} min`;
}

/**
 * The stream on a canvas (docs/m15-live-stats.md 1.1): the latest keyframe every few seconds
 * with Low traffic on, the live view from the node at the source's frame rate with it off
 * (owner, 18.10, item 16; previewHub, liveView.ts). The switch sits in the corner, the same
 * for the whole app and remembered in this browser. Not clickable otherwise. The box is 16:9;
 * any other frame shape is letterboxed. `width`/`height` are the CSS size on a phone; `fluid`
 * fills the parent's width (the map scales).
 */
export function StreamPreview({ stream, available, now, width, height, fluid, frameLabel, aspect = '16 / 9', plain }: {
  stream: string; available: boolean; now: number; width: number; height: number; fluid?: boolean;
  /** fluid box shape: 16 / 9, or 9 / 16 for the vertical input (VERT-1, DS: VT-01) */
  aspect?: string;
  /** no plates of its own (the caller draws its chip and numbers on top): no age, no Low traffic switch */
  plain?: boolean;
  /** the Studio stage: an honest "FRAME N s AGO" when it shows keyframes */
  frameLabel?: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [snap, setSnap] = useState<PreviewSnapshot>();
  const [live, setLive] = useState<LiveViewSnapshot>({ state: 'idle' });
  const [low, setLow] = useState(lowTraffic());
  const [fps, setFps] = useState<number>();
  // The preview of a stream is shared by the whole app (previewHub): leaving a page keeps
  // the frame, the request timing and the live view; the next page shows the picture at once.
  const shared = useMemo(() => sharedPreview(stream, available), [stream]); // a new stream starts over; available is passed on below
  const playing = live.state === 'playing';

  useEffect(() => {
    shared.hold();
    const draw = (s: PreviewSnapshot) => {
      setSnap(s);
      if (s.hasFrame && canvas.current && shared.live.snap.state !== 'playing') blit(shared.canvas, canvas.current);
    };
    draw(shared.snap);
    setLive(shared.live.snap);
    const off = shared.subscribe(draw);
    const offLive = shared.subscribeLive(setLive);
    const offLow = onLowTraffic(setLow);
    return () => {
      off();
      offLive();
      offLow();
      shared.release();
    };
  }, [shared]);
  useEffect(() => shared.setAvailable(available), [shared, available]);

  // The live view: the shared <video> itself in this box (the browser's own size and frame
  // timing, sharp on a big stage). It is in one place at a time: a second box on screen for the
  // same stream copies its frames onto its canvas, and takes the video when the first one goes.
  const host = useRef<HTMLDivElement>(null);
  const [own, setOwn] = useState(false);
  useEffect(() => {
    if (!playing) {
      setFps(undefined);
      setOwn(false);
      if (shared.snap.hasFrame && canvas.current) blit(shared.canvas, canvas.current);
      return;
    }
    const v = shared.live.video;
    let stop = false;
    let handle = 0;
    const times: number[] = [];
    const adopt = () => {
      if (stop || !host.current || v.isConnected) return;
      host.current.appendChild(v);
      // moving a media element out of the page pauses it
      void v.play().catch(() => undefined);
      setOwn(true);
    };
    const paint = (t: number) => {
      if (stop) return;
      const c = canvas.current;
      if (v.parentElement !== host.current && c && v.videoWidth) drawContain(c, v, v.videoWidth, v.videoHeight);
      times.push(t);
      while (times.length && t - times[0]! > 1000) times.shift();
      next();
    };
    const rvfc = (v as HTMLVideoElement & { requestVideoFrameCallback?: (cb: (t: number) => void) => number }).requestVideoFrameCallback?.bind(v);
    const next = () => {
      handle = rvfc ? rvfc(paint) : requestAnimationFrame(paint);
    };
    adopt();
    next();
    const tick = setInterval(() => {
      adopt();
      setFps(times.length);
    }, 1000);
    return () => {
      stop = true;
      clearInterval(tick);
      if (!rvfc) cancelAnimationFrame(handle);
      if (v.parentElement === host.current) v.remove();
      setOwn(false);
    };
  }, [playing, shared]);

  const state = previewState(snap, now);
  if (state === null) return null;
  const age = snap?.frameAt !== undefined ? now - snap.frameAt : undefined;
  const shown = playing || state === 'fresh' || state === 'old' || (state === 'paused' && snap?.hasFrame);
  const label = playing
    ? t('preview.liveLabel', { fps: fps ?? '…' })
    : state === 'unsupported' ? "Stream preview isn't available in this browser"
    : state === 'waiting' ? 'Stream preview: the first frame comes with the next keyframe'
    : state === 'paused' ? 'Stream preview, paused while the tab is hidden'
    : `Stream preview, updated ${ago(age ?? 0)} ago${state === 'old' ? ', old' : ''}`;
  const dpr = 2; // fixed backing size: sharp on retina, cheap to clear
  const small = !fluid && width < 200;
  const notice = live.state === 'busy' ? t('preview.busy') : live.state === 'taken' ? t('preview.taken') : live.state === 'idle_timeout' ? t('preview.idle') : undefined;

  return (
    <div
      data-testid="source-preview"
      data-mode={playing ? 'live' : (snap?.mode ?? 'webcodecs')}
      data-state={playing ? 'live' : state}
      data-live={live.state}
      data-low={low ? 'true' : 'false'}
      data-age={age !== undefined ? Math.round(age / 1000) : ''}
      className="relative flex-none overflow-hidden rounded-lg border border-line bg-[#05080C]"
      style={fluid ? { width: '100%', aspectRatio: aspect } : { width, height }}
    >
      <canvas
        ref={canvas}
        role="img"
        aria-label={label}
        width={width * dpr}
        height={height * dpr}
        className={`block h-full w-full ${shown ? '' : 'invisible'} ${!playing && (state === 'old' || state === 'paused' || notice) ? 'opacity-50' : ''}`}
      />
      {/* the live view's <video> while it plays here; the canvas under it keeps the last keyframe */}
      <div ref={host} data-testid="preview-video" data-own={own ? 'true' : 'false'} aria-hidden="true" className="pointer-events-none absolute inset-0" />
      {!low && !playing && live.state === 'unavailable' && !small && !plain && (
        <span data-testid="preview-why" role="status" title={t('preview.unavailable', { why: live.why ?? '' })}
          className="absolute bottom-1.5 right-1.5 inline-flex max-w-[calc(100%-150px)] items-center gap-1.5 truncate rounded-md bg-overlay px-2 py-1 font-mono text-[10px] font-medium tracking-[.06em] uppercase text-warn backdrop-blur-[6px]">
          <Icon name="state-warn" size={12} />{t('preview.whyShort', { why: live.why ?? '' })}
        </span>
      )}
      {!playing && (state === 'waiting' || state === 'unsupported') && !notice && (
        <span aria-hidden="true" className="absolute inset-0 grid place-items-center px-3 text-center text-[11px] leading-snug text-muted">
          {state === 'waiting' ? 'First frame comes with the next keyframe' : "Preview isn't available in this browser"}
        </span>
      )}
      {notice && (
        <div data-testid="preview-notice" role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-bg/70 px-3 text-center">
          <span className={`leading-snug text-text-soft ${small ? 'text-[10px]' : 'text-xs'}`}>{notice}</span>
          <button type="button" data-testid="preview-start" onClick={() => shared.startViewing()}
            className={`sx-btn ${small ? 'h-7 px-2 text-[11px]' : 'sm'}`}>
            {live.state === 'idle_timeout' ? t('preview.resume') : t('preview.start')}
          </button>
        </div>
      )}
      {plain ? null : playing ? (
        <span aria-hidden="true" data-testid="preview-live-badge"
          className="tabular absolute bottom-1.5 left-1.5 inline-flex items-center gap-1.5 rounded-md bg-overlay px-2 py-1 font-mono text-[10px] font-medium tracking-[.06em] text-live backdrop-blur-[6px]">
          {/* a still state icon: the header logo is the view's one heartbeat (07-motion, MO-01) */}
          <Icon name="state-live" size={12} />
          {t('preview.liveBadge', { fps: fps ?? '…' })}
        </span>
      ) : state !== 'waiting' && state !== 'unsupported' && !notice && (
        <span aria-hidden="true"
          className={`tabular absolute bottom-1.5 left-1.5 inline-flex items-center gap-1.5 rounded-md bg-overlay px-2 py-1 font-mono text-[10px] font-medium tracking-[.06em] backdrop-blur-[6px] ${state === 'old' ? 'text-warn' : 'text-text-soft'}`}>
          {state === 'fresh' && <Icon name="state-live" size={12} className="text-live" />}
          {state === 'old' && <Icon name="state-warn" size={12} />}
          {state === 'paused' ? 'PAUSED · TAB HIDDEN' : `${frameLabel ? 'FRAME ' : ''}${ago(age ?? 0)} AGO${state === 'old' ? ' · OLD' : ''}`}
        </span>
      )}
      {state !== 'unsupported' && !plain && (
        <button type="button" data-testid="preview-low" aria-pressed={low} onClick={() => setLowTraffic(!low)}
          title={low ? t('preview.lowOnTitle') : live.state === 'unavailable' ? t('preview.unavailable', { why: live.why ?? '' }) : t('preview.lowOffTitle')}
          className={`absolute top-1.5 right-1.5 inline-flex items-center gap-1.5 rounded-md border bg-overlay font-mono font-medium tracking-[.06em] backdrop-blur-[6px] focus-visible:shadow-focus ${small ? 'min-h-6 px-1 text-[9px]' : 'min-h-7 px-2 text-[10px]'} ${low ? 'border-accent/45 text-accent' : 'border-transparent text-muted hover:text-text-soft'}`}>
          <Icon name={low ? 'eye-off' : 'eye'} size={12} />
          {t('preview.low')}
          {!low && live.state === 'unavailable' && <Icon name="state-warn" size={12} className="text-warn" />}
        </button>
      )}
    </div>
  );
}
